import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { Flex } from 'rebass/styled-components';
import styled from 'styled-components/macro';

import { rem } from '@blueprint/ui';

import { Typography, ButtonIcon, ExpandLink } from 'components/blueprint';
import { useSafeLabel } from 'hooks';
import { breakpoints, colors, oneColumnLayout } from 'themes';
import { useChatbotQueryResponseState } from 'state/chatbotQueryResponseState';
import { useConversation } from 'state/conversation';
import {
  useIsSupportPanelOpen,
  useSetIsSupportPanelOpen,
} from 'state/isSupportPanelOpen';
import {
  useTriggerChatWindow,
  useSetTriggerChatWindow,
} from 'state/triggerChatWindow';
import { useIsNetChannel } from 'components/ChannelFooter/useIsNetChannel';
import { LoadingSpinner } from 'components/LoadingSpinner/LoadingSpinner';
import { useSubscribe } from 'hooks/useSubscribe';
import { EVENT_FREE_TEXT_QUESTION } from 'utils/chatBotGlobal';
import { SupportFooter } from 'components/ChannelFooter/SupportFooter';
import { isPromoteInputFeedbackReply } from 'components/Reply/reply-type';
import { useHasAgentConversationStarted } from 'state/hasAgentConversationStarted';
import { useIsOneColumnLayout } from 'hooks/useLayoutKind';
import { LEFT_COLUMN_WIDTH_PERCENT } from 'components/layout-dimensions';
import { PANEL_TRIGGER_BUTTON_ID } from 'components/GetSupportButton';
import { useAppConfig } from 'state/appConfig';
import { useGenesysError } from 'genesys/error/error-state';
import { checkDeploymentIdStatus } from 'genesys/genesys';
import { useIsGenesysReAuthenticating } from 'hooks/useIsGenesysReAuthenticating';
import { useChatbotError, useIsChatbotWelcomeError } from 'state/chatbot-error';
import { useChatbotQueryContext } from 'state/chatbotQueryResponseState';
import { useInteractionTracking } from 'tracking/hooks/useInteractionTracking';

import HorizontalRule from '../Ruler/HorizontalRule';
import { ChatWithUs } from '../ChatWithUs/ChatWithUs';
import {
  useChatbotQuery,
  useChatbotInitialContextQuery,
  useGlobalChatbotQuery,
} from './useChatbotQuery';
import { FaqQuestions } from './FaqQuestions';
import { Category, CATEGORY_SELECT_FIELD_ID } from './Category';
import { Breadcrumbs } from './Breadcrumbs';
import messages from './messages';
import { EntryInput } from './EntryInput/EntryInput';
import { ConversationPanel } from './ConversationPanel/ConversationPanel';
import {
  useChatbotInitialQuerySetConversationEffect,
  useChatbotQueryResponseEffect,
  useSetChatbotQueryResponseStateEffect,
} from './useChatbotQueryResponseEffect';
import { SystemErrorNotification } from './SystemErrorNotification';
import { getErrorMessage } from './getErrorMessage';
import { useSubscribeChatbotQueryErrorAndSetState } from './useSubscribeChatbotQueryErrorAndSetState';

const { grey } = colors;

const StyledExpand = styled(ExpandLink)`
  @media (min-width: ${breakpoints.md}px) {
    && {
      display: none;
    }
  }

  @media (max-width: ${breakpoints.md}px) {
    && {
      margin: 0;
    }
  }
`;

const HelpCategoryText = styled(Typography)`
  @media (min-width: ${breakpoints.md}px) {
    && {
      margin: 0;
    }
  }

  @media (max-width: ${breakpoints.md}px) {
    && {
      display: none;
    }
  }
`;

export const CHAT_UNAVAILABLE_ENTRY = 'livechatstate2';

/**
 * SupportPanel
 */
// eslint-disable-next-line complexity
export const SupportPanel = () => {
  const safeLabel = useSafeLabel();
  const [showCategory, setShowCategory] = useState(false);
  const [deploymentIdStatus, setDeploymentIdStatus] = useState(true);
  const { messagingStatus } = useAppConfig();
  const trackNotification = useInteractionTracking('notification');

  const isOneColumnLayout = useIsOneColumnLayout();

  const conversation = useConversation();
  const chatbotQueryResponseState = useChatbotQueryResponseState();
  const isSupportPanelOpen = useIsSupportPanelOpen();
  const triggerChatWindow = useTriggerChatWindow();
  const hasAgentConversationStarted = useHasAgentConversationStarted();
  const { hasQueryContext } = useChatbotQueryContext();

  const setIsSupportPanelOpened = useSetIsSupportPanelOpen();
  const setTriggerChatWindow = useSetTriggerChatWindow();
  const [initialConversation] = conversation;

  useEffect(() => {
    if (!isSupportPanelOpen) {
      const asyncCheckDeploymentIdStatus = async () => {
        const showMessaging = await checkDeploymentIdStatus();

        setDeploymentIdStatus(showMessaging);
      };

      asyncCheckDeploymentIdStatus();
    }
  }, [isSupportPanelOpen]);

  const previousOpenessRef = useRef(isSupportPanelOpen);

  const hasChatbotBeenInteracted = () =>
    conversation?.length > 1 ||
    (conversation?.length === 1 && !!initialConversation.question);

  const hasGenesysEnabled = () =>
    messagingStatus === 'genesys' && !!deploymentIdStatus;

  const messagingServiceEnabled =
    hasGenesysEnabled() &&
    (hasChatbotBeenInteracted() || !!hasAgentConversationStarted);

  const {
    data: globalChatbotQueryResponse,
    isFetching: isGlobalChatbotQueryFetching,
    sendGlobalChatbotQuery,
  } = useGlobalChatbotQuery();

  useChatbotQueryResponseEffect(globalChatbotQueryResponse);

  const {
    data: chatbotInitialContextResponse,
    isFetching: isInitialContextFetching,
    isError: isChatBotInitialError,
  } = useChatbotInitialContextQuery({
    enabled: isSupportPanelOpen && !triggerChatWindow?.entry,
  });

  useSetChatbotQueryResponseStateEffect(chatbotInitialContextResponse);
  useChatbotInitialQuerySetConversationEffect(
    chatbotInitialContextResponse,
    conversation.length < 1,
  );

  const {
    data: chatbotQueryResponse,
    sendChatbotQuery,
    isFetching: isConversationLoading,
    isError: isChatBotError,
  } = useChatbotQuery();

  const { isGenesysReAuthenticating } = useIsGenesysReAuthenticating();

  useChatbotQueryResponseEffect(chatbotQueryResponse);

  const isNetChannel = useIsNetChannel();

  const { questions, breadcrumb, dropdown } = chatbotQueryResponseState;

  const isProvideNoFeedbackEntry = isPromoteInputFeedbackReply(
    conversation[conversation.length - 1]?.replyType,
  );

  const handleSelectFaq = ({
    answer_id,
    recognition_id,
    faqrequest,
    selectSource,
    subQuery = {},
  }) => {
    sendChatbotQuery({
      ANSWER_ID: answer_id,
      RECOGNITION_ID: recognition_id,
      FAQRequest: faqrequest,
      selectSource,
      FAQ: 1,
      ...subQuery,
    });
  };

  const handleNavContextChange = (navcontext) => {
    sendChatbotQuery({
      FAQ: 1,
      ButtonRequest: 'change_Context',
      navcontext,
    });
  };

  /*
   * Handle free text input query send botquery accordingly
   */
  const handleSendEntryQuery = useCallback(
    (entry) => {
      if (hasQueryContext) {
        sendChatbotQuery({ entry });
      } else {
        sendGlobalChatbotQuery({ entry });
      }
    },
    [sendChatbotQuery, sendGlobalChatbotQuery, hasQueryContext],
  );

  const handleSendFreeTextFeedback = (entry) => {
    const { dtreenodeid, dtreeobjectid } = chatbotQueryResponse ?? {};

    sendChatbotQuery({
      entry,
      DTreeRequestType: 0,
      DTREE_OBJECT_ID: dtreeobjectid,
      DTREE_NODE_ID: dtreenodeid,
    });
  };

  /*
   * Handle link clicks on the answer and make a free text query
   */
  const handleAskQuestionEvent = useCallback(
    (topic, entry) => {
      handleSendEntryQuery(entry);
    },
    [handleSendEntryQuery],
  );

  /*
   * Handle ask_question global event
   */
  useSubscribe(EVENT_FREE_TEXT_QUESTION, handleAskQuestionEvent);

  const handleFeedback = (feedback) => sendChatbotQuery(feedback);

  const handleClosingSupportPanel = () => setIsSupportPanelOpened(false);

  const handleOnUnavailableClick = () =>
    sendChatbotQuery({ entry: CHAT_UNAVAILABLE_ENTRY });

  /*
   * Focus back when panel close
   */
  useLayoutEffect(() => {
    const shouldFocusBack =
      previousOpenessRef.current === true && isSupportPanelOpen === false;

    if (shouldFocusBack) {
      const toFocusElement =
        triggerChatWindow.triggerElement ||
        document.getElementById(PANEL_TRIGGER_BUTTON_ID);

      setTriggerChatWindow({});
      toFocusElement?.focus();
    }

    previousOpenessRef.current = isSupportPanelOpen;
  }, [
    isSupportPanelOpen,
    triggerChatWindow.triggerElement,
    setTriggerChatWindow,
  ]);

  useSubscribeChatbotQueryErrorAndSetState(
    isSupportPanelOpen,
    isChatBotInitialError,
    isChatBotError,
  );

  const chatbotError = useChatbotError();
  const isChatbotWelcomeError = useIsChatbotWelcomeError();
  const genesysError = useGenesysError();

  const errorMessage = useMemo(
    () => getErrorMessage(chatbotError, genesysError),
    [chatbotError, genesysError],
  );

  useEffect(() => {
    if (errorMessage) {
      const messageValues = errorMessage[1];

      trackNotification({
        tealiumData: {
          event_content: `error received: ${messageValues.errorCode}`,
        },
      })();
    }
  }, [errorMessage, trackNotification]);

  const isFirstChatbotQueryFetching =
    isInitialContextFetching || isGlobalChatbotQueryFetching;

  return (
    <>
      {errorMessage && (
        <SystemErrorNotification message={safeLabel(...errorMessage)} />
      )}
      <Flex
        flexGrow={1}
        flexShrink={1}
        minHeight={0}
        sx={{
          [`@media screen and (max-width: ${breakpoints.md}px)`]: {
            flexDirection: 'column-reverse',
            justifyContent: 'flex-end',
            minHeight: 'auto',
          },
        }}
      >
        {isFirstChatbotQueryFetching ? (
          <LoadingSpinner
            label={safeLabel(messages.loading)}
            wrapperSx={{
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            loaderSx={{
              left: !isOneColumnLayout ? rem(470) : null,
            }}
          />
        ) : (
          <>
            <Flex
              flexDirection="column"
              width={LEFT_COLUMN_WIDTH_PERCENT}
              paddingRight={rem(20)}
              sx={{
                borderRight: `${rem(1)} solid`,
                borderColor: grey,
                content: 'none',
                [oneColumnLayout]: {
                  width: '100%',
                  minHeight: 0,
                  paddingRight: 0,
                  borderRight: 0,
                },
              }}
            >
              {isOneColumnLayout && !showCategory && (
                <HorizontalRule sx={{ marginBottom: rem(10) }} />
              )}
              <StyledExpand
                variant="h2"
                fontSize={rem(14)}
                justifyContent="space-between"
                marginLeft={rem(20)}
                onClick={() => setShowCategory(!showCategory)}
                display="absolute"
              >
                {safeLabel(messages.helpCategories)}
              </StyledExpand>

              {(showCategory || !isOneColumnLayout) && (
                <>
                  <HelpCategoryText variant="h2">
                    <Typography
                      as="label"
                      variant="h2"
                      fontWeight="light"
                      htmlFor={CATEGORY_SELECT_FIELD_ID}
                    >
                      {safeLabel(messages.helpCategories)}
                    </Typography>
                  </HelpCategoryText>

                  <Category
                    breadcrumb={breadcrumb}
                    dropdown={dropdown}
                    onNavContextChange={handleNavContextChange}
                    isError={isChatbotWelcomeError}
                  />
                  <Breadcrumbs
                    dropdown={dropdown}
                    breadcrumb={breadcrumb}
                    onNavContextChange={handleNavContextChange}
                  />
                  {!isChatbotWelcomeError && (
                    <HorizontalRule
                      sx={{
                        marginBottom: rem(20),
                        marginTop: rem(20),
                      }}
                    />
                  )}
                  <FaqQuestions
                    questions={questions}
                    onSelectFaq={handleSelectFaq}
                  />
                </>
              )}
              {(messagingServiceEnabled || !!chatbotError) &&
                !isOneColumnLayout && (
                  <>
                    <HorizontalRule sx={{ marginBottom: rem(20) }} />
                    <ChatWithUs onUnavailableClick={handleOnUnavailableClick} />
                  </>
                )}
            </Flex>
            <Flex
              flexGrow={1}
              flexShrink={1}
              minHeight={0}
              flexDirection="column"
              paddingLeft={rem(20)}
              sx={{
                [oneColumnLayout]: {
                  paddingLeft: 0,
                  flexGrow: 0,
                },
              }}
            >
              <Flex justifyContent="space-between">
                <Typography variant="h2" fontWeight="light">
                  {safeLabel(
                    isNetChannel
                      ? messages.virtualAssistant
                      : messages.chatAssistant,
                  )}
                </Typography>
                {!chatbotError && !genesysError && (
                  <ButtonIcon
                    name="Close"
                    iconProps={{
                      titleAccess: safeLabel(messages.buttonTitle),
                    }}
                    titleAccess={safeLabel(messages.buttonTitle)}
                    onClick={handleClosingSupportPanel}
                    aria-label={safeLabel(messages.hideAriaLabel)}
                  />
                )}
              </Flex>
              <HorizontalRule marginBottom={rem(16)} />
              <Flex
                flexDirection="column"
                flexGrow={1}
                flexShrink={1}
                minHeight={0}
                sx={{
                  [oneColumnLayout]: showCategory && {
                    '&&': {
                      display: 'none',
                    },
                  },
                }}
              >
                <ConversationPanel
                  conversation={conversation}
                  onAnswerFaqLinkClick={handleSelectFaq}
                  onFeedback={handleFeedback}
                  display={isSupportPanelOpen}
                  onSkipFeedback={handleSendFreeTextFeedback}
                />
                {(isConversationLoading || isGenesysReAuthenticating) && (
                  <LoadingSpinner
                    bottomLoader
                    label={safeLabel(
                      isGenesysReAuthenticating
                        ? messages.genesysReAuthenticating
                        : messages.chatbotQueryLoading,
                    )}
                    spinnerProps={{
                      hideLabel: true,
                    }}
                    wrapperSx={{
                      justifyContent: 'end',
                      alignItems: 'center',
                    }}
                    loaderSx={{
                      right: rem(40),
                    }}
                  />
                )}
                <EntryInput
                  onSendQuery={
                    isProvideNoFeedbackEntry
                      ? handleSendFreeTextFeedback
                      : handleSendEntryQuery
                  }
                  marginTop={rem(10)}
                  onTypeAheadSelect={handleSelectFaq}
                  isError={isChatbotWelcomeError}
                />

                {(messagingServiceEnabled || !!chatbotError) &&
                  isOneColumnLayout && (
                    <ChatWithUs onUnavailableClick={handleOnUnavailableClick} />
                  )}
              </Flex>
            </Flex>
          </>
        )}
      </Flex>
      {isNetChannel && (
        <SupportFooter marginTop={rem(20)} onLinkClick={handleSendEntryQuery} />
      )}
    </>
  );
};
